import { Search } from 'lucide-react'
import React, { useEffect, useRef } from 'react'
import Autocomplete from 'react-google-autocomplete'

import { useMapApiKey } from 'src/api/maps'

interface LocationAutoCompleteProps {
  onPlaceSelected: (place: PlaceResult) => void
  placeholder?: string
  types: string[]
  componentRestrictions?: google.maps.places.ComponentRestrictions
  inputAutocompleteValue?: string
  refValue: 'street' | 'city' | ''
  value?: string
}

export interface PlaceResult {
  street: string
  city: string
  state: string
  zip: string
  latitude: number
  longitude: number
}

export const LocationAutoComplete: React.FC<LocationAutoCompleteProps> = ({
  onPlaceSelected,
  placeholder = 'Enter a location',
  types = ['address'],
  componentRestrictions = { country: 'us' },
  inputAutocompleteValue,
  refValue,
  value,
}) => {
  const autocompleteRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (autocompleteRef.current) {
      autocompleteRef.current.value = value || ''
    }
  }, [value])

  const handlePlaceSelected = (place: google.maps.places.PlaceResult) => {
    const addressComponents = place.address_components || []
    const geometry = place.geometry || {}

    const getComponent = (types: string[]) => {
      for (const type of types) {
        const component = addressComponents.find((component) => component.types.includes(type))
        if (component) {
          if (type === 'administrative_area_level_1' || type === 'administrative_area_level_2') {
            return component.short_name
          }
          return component.long_name
        }
      }
      return ''
    }

    const streetNumber = getComponent(['street_number'])
    const route = getComponent(['route'])
    const street = `${streetNumber} ${route}`.trim()
    const city = getComponent(['locality', 'postal_town', 'sublocality', 'neighborhood', 'administrative_area_level_2'])
    const state = getComponent(['administrative_area_level_1', 'administrative_area_level_2'])
    const zip = getComponent(['postal_code'])

    const placeResult: PlaceResult = {
      street,
      city,
      state,
      zip,
      latitude: geometry.location?.lat() || 0,
      longitude: geometry.location?.lng() || 0,
    }

    onPlaceSelected(placeResult)

    if (autocompleteRef.current) {
      switch (refValue) {
        case 'street':
          autocompleteRef.current.value = street
          break
        case 'city':
          autocompleteRef.current.value = `${city}, ${state}`
          break
        default:
          autocompleteRef.current.value = ''
      }
    }
  }

  const { data: mapApiKey } = useMapApiKey()
  //TODO: refactor - it is making the apikey public
  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-primary" />
      <Autocomplete
        ref={autocompleteRef}
        apiKey={mapApiKey}
        onPlaceSelected={handlePlaceSelected}
        options={{
          types,
          componentRestrictions,
        }}
        className="peer flex h-14 w-full rounded-md border border-input bg-background py-2 pl-10 pr-3 ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted focus-visible:border-2 focus-visible:border-primary focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        placeholder=" "
        inputAutocompleteValue={inputAutocompleteValue}
      />
      <label className="pointer-events-none absolute left-10 top-2 origin-[0] -translate-y-4 scale-75 bg-background px-1 text-sm text-muted transition-all duration-300 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-placeholder-shown:text-base peer-focus:top-2 peer-focus:-translate-y-4 peer-focus:scale-75">
        {placeholder}
      </label>
    </div>
  )
}
